<script lang="ts" setup>
import { ref, watch, watchEffect } from 'vue'
import type {
  LineRichMenuFormBackgroundImage,
  LineRichMenuFormTemplateName,
} from './formSchema'
import IconClose from '@/components/icons/IconClose.vue'

const model = defineModel<LineRichMenuFormBackgroundImage | null>({
  required: true,
})

const props = defineProps<{
  templateName: LineRichMenuFormTemplateName
}>()

const imageSrc = ref<string | null>(null)

watchEffect(async () => {
  if (!model.value) {
    imageSrc.value = null
    return
  }

  if (typeof model.value.image === 'string') {
    imageSrc.value = model.value.image
    return
  }

  const img = await buildImage(model.value.image)
  imageSrc.value = img.src
})

const handleFileInput = async (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files?.[0] == undefined) {
    return
  }

  const file = target.files[0]

  // input の持つファイルをクリアしている
  // これをしないと以下の場合にこまる
  //   - A ファイルを選択する
  //   - dolphin 上からファイルを削除する
  //   - A ファイルを選択する
  // このとき、A ファイルが選択されたイベントが発火しない
  // なぜなら削除は Vue の状態の上での削除であり、input 要素で保持しているから
  // よって、処理をシンプルにするために、input 要素のファイルを毎回クリアする
  target.value = ''

  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    alert('画像の形式は PNG または JPEG である必要があります。')
    return
  }

  const maxSize = 1024 * 1024 // 1MB
  if (file.size > maxSize) {
    alert('画像のサイズは 1MB 以下である必要があります。')
    return
  }

  const img = await buildImage(file)

  switch (true) {
    case props.templateName.includes('large'):
      if (img.width !== 1200 || img.height !== 810) {
        alert('画像のサイズは横幅 1200px 縦幅 810px である必要があります。')
        return
      }

      break
    case props.templateName.includes('small'):
      if (img.width !== 1200 || img.height !== 405) {
        alert('画像のサイズは横幅 1200px 縦幅 405px である必要があります。')
        return
      }
      break
    default:
      alert('テンプレートが不正です')
      return
  }

  model.value = {
    name: null,
    image: file,
    size: props.templateName.includes('large') ? 'large' : 'small',
  }
}

const buildImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      resolve(img)
    }

    img.onerror = error => {
      reject(error)
    }

    img.src = URL.createObjectURL(file)
  })
}

const handleSelectedImageRemove = () => {
  model.value = null
}

const image = ref<HTMLImageElement | null>(null)
const verticalLine1 = ref<HTMLDivElement | null>(null)
const verticalLine2 = ref<HTMLDivElement | null>(null)
const horizontalLine = ref<HTMLDivElement | null>(null)
const panelA = ref<HTMLDivElement | null>(null)
const panelB = ref<HTMLDivElement | null>(null)
const panelC = ref<HTMLDivElement | null>(null)
const panelD = ref<HTMLDivElement | null>(null)
const panelE = ref<HTMLDivElement | null>(null)
const panelF = ref<HTMLDivElement | null>(null)

const updateLinePosition = () => {
  if (
    image.value &&
    verticalLine1.value &&
    verticalLine2.value &&
    horizontalLine.value &&
    panelA.value &&
    panelB.value &&
    panelC.value &&
    panelD.value &&
    panelE.value &&
    panelF.value
  ) {
    // 初期化
    verticalLine1.value.style.display = 'block'
    verticalLine1.value.style.left = '0'
    verticalLine1.value.style.top = '0'
    verticalLine1.value.style.height = '0'
    verticalLine2.value.style.display = 'block'
    verticalLine2.value.style.left = '0'
    verticalLine2.value.style.top = '0'
    verticalLine2.value.style.height = '0'
    horizontalLine.value.style.display = 'block'
    horizontalLine.value.style.left = '0'
    horizontalLine.value.style.top = '0'
    horizontalLine.value.style.width = '0'
    panelA.value.style.display = 'block'
    panelA.value.style.left = '0'
    panelA.value.style.top = '0'
    panelB.value.style.display = 'block'
    panelB.value.style.left = '0'
    panelB.value.style.top = '0'
    panelC.value.style.display = 'block'
    panelC.value.style.left = '0'
    panelC.value.style.top = '0'
    panelD.value.style.display = 'block'
    panelD.value.style.left = '0'
    panelD.value.style.top = '0'
    panelE.value.style.display = 'block'
    panelE.value.style.left = '0'
    panelE.value.style.top = '0'
    panelF.value.style.display = 'block'
    panelF.value.style.left = '0'
    panelF.value.style.top = '0'

    const imgWidth = image.value.clientWidth
    const imgHeight = image.value.clientHeight
    const imgNaturalWidth = image.value.naturalWidth
    const imgNaturalHeight = image.value.naturalHeight
    // eslint-disable-next-line fp/no-let
    let verticalLine1Position: number
    // eslint-disable-next-line fp/no-let
    let verticalLine2Position: number
    // eslint-disable-next-line fp/no-let
    let horizontalLinePosition: number

    // テンプレートごとに線の位置を設定
    switch (props.templateName) {
      case 'large_201': // 6つのアクション
        // バリデーション
        if (imgNaturalWidth !== 1200 || imgNaturalHeight !== 810) {
          alert('画像のサイズは横幅 1200px 縦幅 810px である必要があります')
          return
        }
        verticalLine1Position = (400 / 1200) * imgWidth // 左から400pxの位置
        verticalLine2Position = (800 / 1200) * imgWidth // 左から800pxの位置
        horizontalLinePosition = (405 / 810) * imgHeight // 縦中央の位置
        // 縦線1本目
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 縦線2本目
        verticalLine2.value.style.left = `${verticalLine2Position}px`
        verticalLine2.value.style.height = `${imgHeight}px`
        // 横線
        horizontalLine.value.style.top = `${horizontalLinePosition}px`
        horizontalLine.value.style.width = `${imgWidth}px`
        // 左上
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${horizontalLinePosition / 2}px`
        // 真ん中上
        panelB.value.style.left = `${(verticalLine1Position + verticalLine2Position) / 2}px`
        panelB.value.style.top = `${horizontalLinePosition / 2}px`
        // 右上
        panelC.value.style.left = `${(verticalLine2Position + imgWidth) / 2}px`
        panelC.value.style.top = `${horizontalLinePosition / 2}px`
        // 左下
        panelD.value.style.left = `${verticalLine1Position / 2}px`
        panelD.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 真ん中下
        panelE.value.style.left = `${(verticalLine1Position + verticalLine2Position) / 2}px`
        panelE.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 右下
        panelF.value.style.left = `${(verticalLine2Position + imgWidth) / 2}px`
        panelF.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`

        break
      case 'large_202': // 4つのアクション
        verticalLine1Position = (600 / 1200) * imgWidth // 横中央の位置
        horizontalLinePosition = (405 / 810) * imgHeight // 縦中央の位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 横線の位置と幅を設定
        horizontalLine.value.style.top = `${horizontalLinePosition}px`
        horizontalLine.value.style.width = `${imgWidth}px`
        // 左上
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${horizontalLinePosition / 2}px`
        // 右上
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${horizontalLinePosition / 2}px`
        // 左下
        panelC.value.style.left = `${verticalLine1Position / 2}px`
        panelC.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 右下
        panelD.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelD.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'large_203': // 4つのアクション
        verticalLine1Position = (400 / 1200) * imgWidth // 左から400pxの位置
        verticalLine2Position = (800 / 1200) * imgWidth // 左から800pxの位置
        horizontalLinePosition = (405 / 810) * imgHeight // 縦中央の位置
        // 縦線1本目は左から400pxの位置で縦が画像の下半分のみ
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.top = `${imgHeight / 2}px`
        verticalLine1.value.style.height = `${imgHeight / 2}px`
        // 縦線2本目は左から800pxの位置で縦が画像の下半分のみ
        verticalLine2.value.style.left = `${verticalLine2Position}px`
        verticalLine2.value.style.top = `${imgHeight / 2}px`
        verticalLine2.value.style.height = `${imgHeight / 2}px`
        // 横線は縦中央の位置で横が画像の横幅
        horizontalLine.value.style.top = `${horizontalLinePosition}px`
        horizontalLine.value.style.width = `${imgWidth}px`
        // 上
        panelA.value.style.left = `${imgWidth / 2}px`
        panelA.value.style.top = `${horizontalLinePosition / 2}px`
        // 左下
        panelB.value.style.left = `${verticalLine1Position / 2}px`
        panelB.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 真ん中下
        panelC.value.style.left = `${(verticalLine1Position + verticalLine2Position) / 2}px`
        panelC.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 右下
        panelD.value.style.left = `${(verticalLine2Position + imgWidth) / 2}px`
        panelD.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 不要な要素を非表示にする
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'large_204':
        verticalLine1Position = (800 / 1200) * imgWidth // 左から800pxの位置
        horizontalLinePosition = (405 / 810) * imgHeight // 縦中央の位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 横線の位置と幅を設定
        horizontalLine.value.style.left = `${verticalLine1Position}px`
        horizontalLine.value.style.top = `${horizontalLinePosition}px`
        horizontalLine.value.style.width = `${imgWidth - verticalLine1Position}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 右上
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${horizontalLinePosition / 2}px`
        // 右下
        panelC.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelC.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'large_205': // 2つのアクション
        horizontalLinePosition = (405 / 810) * imgHeight // 縦中央の位置
        // 横線の位置と幅を設定
        horizontalLine.value.style.top = `${horizontalLinePosition}px`
        horizontalLine.value.style.width = `${imgWidth}px`
        // 上
        panelA.value.style.left = `${imgWidth / 2}px`
        panelA.value.style.top = `${horizontalLinePosition / 2}px`
        // 下
        panelB.value.style.left = `${imgWidth / 2}px`
        panelB.value.style.top = `${(horizontalLinePosition + imgHeight) / 2}px`
        // 不要な要素を非表示にする
        verticalLine1.value.style.display = 'none'
        verticalLine2.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'large_206': // 2つのアクション
        verticalLine1Position = (600 / 1200) * imgWidth // 横中央の位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 右
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'large_207': // 1つのアクション
        panelA.value.style.left = `${imgWidth / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine1.value.style.display = 'none'
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelB.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'small_101': // 3つのアクション
        verticalLine1Position = (400 / 1200) * imgWidth // 左から400pxの位置
        verticalLine2Position = (800 / 1200) * imgWidth // 左から800pxの位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 縦線2本目
        verticalLine2.value.style.left = `${verticalLine2Position}px`
        verticalLine2.value.style.height = `${imgHeight}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 真ん中
        panelB.value.style.left = `${(verticalLine1Position + verticalLine2Position) / 2}px`
        panelB.value.style.top = `${imgHeight / 2}px`
        // 右
        panelC.value.style.left = `${(verticalLine2Position + imgWidth) / 2}px`
        panelC.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        horizontalLine.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'small_102': // 2つのアクション
        verticalLine1Position = (600 / 1200) * imgWidth // 横中央の位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 右
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'small_103': // 2つのアクション
        verticalLine1Position = (800 / 1200) * imgWidth // 左から800pxの位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 右
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'small_104': // 2つのアクション
        verticalLine1Position = (400 / 1200) * imgWidth // 左から400pxの位置
        // 縦線の位置と高さを設定
        verticalLine1.value.style.left = `${verticalLine1Position}px`
        verticalLine1.value.style.height = `${imgHeight}px`
        // 左
        panelA.value.style.left = `${verticalLine1Position / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 右
        panelB.value.style.left = `${(verticalLine1Position + imgWidth) / 2}px`
        panelB.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
      case 'small_105': // 1つのアクション
        panelA.value.style.left = `${imgWidth / 2}px`
        panelA.value.style.top = `${imgHeight / 2}px`
        // 不要な要素を非表示にする
        verticalLine1.value.style.display = 'none'
        verticalLine2.value.style.display = 'none'
        horizontalLine.value.style.display = 'none'
        panelB.value.style.display = 'none'
        panelC.value.style.display = 'none'
        panelD.value.style.display = 'none'
        panelE.value.style.display = 'none'
        panelF.value.style.display = 'none'

        break
    }
  }
}

// props.templateName が変更されたときに、線の位置を更新する
watch(() => props.templateName, updateLinePosition)
</script>

<template>
  <section>
    <p :class="$style.sectionTitle">画像</p>
    <div :class="$style.imageInput">
      <label v-if="!model" :class="$style.inputLabel">
        画像をアップロードする
        <input
          type="file"
          accept="image/jpeg,image/png"
          @change="handleFileInput"
          :class="$style.input"
        />
      </label>
      <div v-if="imageSrc != null" :class="$style.selectedImageWrapper">
        <img :class="$style.img" :src="imageSrc" alt="" ref="image" />
        <div>
          <IconClose
            :class="$style.removeButton"
            @click="handleSelectedImageRemove"
          />
        </div>
      </div>
      <div v-if="imageSrc" :class="$style.selectedImageWrapper">
        <div :class="$style.overlay"></div>
        <img
          :class="$style.img"
          :src="imageSrc"
          alt=""
          @load="updateLinePosition"
          ref="image"
        />
        <div ref="verticalLine1" :class="$style.verticalLine"></div>
        <div ref="verticalLine2" :class="$style.verticalLine"></div>
        <div ref="horizontalLine" :class="$style.horizontalLine"></div>
        <div ref="panelA" :class="$style.marker">A</div>
        <div ref="panelB" :class="$style.marker">B</div>
        <div ref="panelC" :class="$style.marker">C</div>
        <div ref="panelD" :class="$style.marker">D</div>
        <div ref="panelE" :class="$style.marker">E</div>
        <div ref="panelF" :class="$style.marker">F</div>
      </div>
      <div>
        <div :class="$style.imageRegulation">
          <p>画像はいずれかのサイズをご用意ください。</p>
          <ul>
            <li>大きいリッチメニュー: 810px × 1200px</li>
            <li>小さいリッチメニュー: 405px × 1200px</li>
          </ul>
        </div>
        アップロードされた画像は第三者にも公開される URL
        からアクセスできます。<br />そのためアップロードする画像に個人情報を含めないでください。
      </div>
    </div>
  </section>
</template>

<style lang="scss" module>
.sectionTitle {
  font-size: 18px;
  font-weight: bold;
}

.imageInput {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  border: 1px solid $color-border-low-emphasis;
  margin-top: 16px;
}

.inputLabel {
  text-decoration: underline;
  cursor: pointer;
}

.input {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
}

.selectedImageWrapper {
  position: relative;
  display: flex;
  margin-top: 16px;
}

.img {
  width: 100%;
}

.verticalLine {
  position: absolute;
  top: 0;
  height: 100%;
  width: 1px;
  background-color: $color-white-1000;
}

.horizontalLine {
  position: absolute;
  left: 0;
  height: 1px;
  background-color: $color-white-1000;
}

.removeButton {
  width: 24px;
  height: 24px;
  cursor: pointer;
  margin-left: 8px;
}

.imageRegulation {
  margin: 30px 0;
}

.inputWrapper {
  margin-top: 16px;
}

.textInputLabel {
  font-size: 14px;
  font-weight: bold;
}

.textInputLabelNote {
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;
}

.textInput {
  width: 100%;
  margin-top: 4px;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $color-black-500;
  pointer-events: none;
}

.marker {
  position: absolute;
  color: $color-white-1000;
  font-size: 36px;
  transform: translate(-50%, -50%);
}
</style>
